import { Building2, Users, Award, TrendingUp, Shield, Home as HomeIcon, FileText, Briefcase } from 'lucide-react';
import Navbar from '../components/Navbar';

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-primary text-white py-20" data-testid="about-hero">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
              About True Bridge
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-light leading-relaxed">
              Your Trusted Partner in Real Estate and Financial Services
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-4xl font-bold">25+</p>
                <p className="text-sm text-white/80">Years Experience</p>
              </div>
              <div className="h-12 w-px bg-white/30"></div>
              <div className="text-center">
                <p className="text-4xl font-bold">100%</p>
                <p className="text-sm text-white/80">Client Satisfaction</p>
              </div>
              <div className="h-12 w-px bg-white/30"></div>
              <div className="text-center">
                <p className="text-4xl font-bold">Expert</p>
                <p className="text-sm text-white/80">Professional Team</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white" data-testid="our-story">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold">Our Story</h2>
              </div>
              <p className="text-lg leading-relaxed text-slate-700 mb-6">
                <strong>True Bridge India Finance Service Pvt Ltd</strong> is a dynamic and innovative financial services and real estate company, founded by seasoned professionals with a collective experience of over 25 years in the banking and financial sector, particularly in the lending division.
              </p>
              <p className="text-lg leading-relaxed text-slate-700 mb-6">
                Leveraging our extensive expertise and strong network, we have built a reputation for providing comprehensive financial solutions that empower individuals and businesses to achieve their real estate and financial goals.
              </p>
              <p className="text-lg leading-relaxed text-slate-700">
                Our journey began with a simple yet powerful vision: to bridge the gap between financial aspirations and real estate opportunities, creating lasting value for our clients across Kerala and beyond.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf"
                alt="Our Team"
                className="rounded-lg shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-secondary text-white p-6 rounded-lg shadow-lg max-w-xs">
                <p className="text-3xl font-bold mb-1">25+ Years</p>
                <p className="text-sm">of Combined Expertise in Banking & Finance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-20 bg-slate-50" data-testid="our-expertise">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">Our Expertise</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive solutions across real estate, finance, and property management
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Mortgage Services */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300" data-testid="expertise-mortgage">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <HomeIcon className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Mortgage Services</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                With decades of experience in the mortgage industry, our team provides expert guidance and support to clients seeking:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Home loans with competitive rates and flexible terms</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Commercial property loans for business expansion</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Residential property loans tailored to your needs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Comprehensive mortgage-related services and consultation</span>
                </li>
              </ul>
            </div>

            {/* Real Estate */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300" data-testid="expertise-realestate">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Real Estate</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Our team possesses extensive knowledge of Kerala's real estate market, enabling us to offer valuable insights and strategic advice to clients looking to:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-secondary mt-1">•</span>
                  <span>Buy residential or commercial properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary mt-1">•</span>
                  <span>Sell properties at optimal market value</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary mt-1">•</span>
                  <span>Invest strategically in high-growth areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary mt-1">•</span>
                  <span>Navigate new launches from premium builders</span>
                </li>
              </ul>
            </div>

            {/* Legal Services */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300" data-testid="expertise-legal">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Legal Services</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We maintain strong partnerships with experienced legal professionals, ensuring our clients receive expert advice and comprehensive support in:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>Property documentation and title verification</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>Legal due diligence for property transactions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>Financial agreement structuring and compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>Dispute resolution and legal consultation</span>
                </li>
              </ul>
            </div>

            {/* Property Management */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300" data-testid="expertise-management">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Property Management</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Our property management services are designed to help property owners maximize their investment's potential while minimizing hassle and stress:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Tenant screening and placement services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Rent collection and financial reporting</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Property maintenance and repairs coordination</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Investment optimization strategies</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Strengths */}
      <section className="py-20 bg-white" data-testid="our-strengths">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">Our Strengths</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              What sets us apart in the industry
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Strong Customer Relations */}
            <div className="text-center" data-testid="strength-relations">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Strong Customer Relations</h3>
              <p className="text-slate-700 leading-relaxed">
                We have cultivated a robust network of relationships with high net worth individuals (HNIs) and diverse clients. Our focus on customer acquisition, personalized product service, and long-term retention has created lasting partnerships built on trust and mutual success.
              </p>
            </div>

            {/* Customer Satisfaction */}
            <div className="text-center" data-testid="strength-satisfaction">
              <div className="w-20 h-20 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-secondary" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Customer Satisfaction</h3>
              <p className="text-slate-700 leading-relaxed">
                Our unwavering commitment to delivering exceptional service and tailored solutions has earned us a sterling reputation for high client satisfaction. We don't just meet expectations—we exceed them, ensuring every client's journey with us is smooth, transparent, and rewarding.
              </p>
            </div>

            {/* Expert Team */}
            <div className="text-center" data-testid="strength-team">
              <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold mb-4">Expert Team</h3>
              <p className="text-slate-700 leading-relaxed">
                Our team comprises seasoned professionals with deep understanding of the financial services industry, particularly in mortgage and real estate. With collective experience spanning over 25 years, we bring invaluable insights, proven strategies, and dedication to every client engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Personnel */}
      <section className="py-20 bg-slate-50" data-testid="key-personnel">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">Key Personnel</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Meet the visionary leader driving True Bridge forward
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-100 hover:shadow-xl transition-all duration-300">
              <div className="md:flex">
                <div className="md:w-1/3 bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center p-8">
                  <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center">
                    <Users className="w-16 h-16 text-white" />
                  </div>
                </div>
                <div className="md:w-2/3 p-8">
                  <h3 className="text-2xl font-heading font-bold mb-2">Abhilash V R</h3>
                  <p className="text-secondary font-semibold mb-4">Founder & Director</p>
                  <p className="text-slate-700 leading-relaxed mb-6">
                    With over 25 years of extensive experience in the banking and financial services sector, Abhilash V R brings deep expertise in lending, mortgage services, and real estate. His vision and leadership have been instrumental in establishing True Bridge as a trusted name in Kerala's real estate and financial services industry.
                  </p>
                  <a
                    href="http://linkedin.com/in/abhilash-v-r-07a78816b"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A66C2] text-white rounded-lg hover:bg-[#004182] transition-colors font-medium"
                    data-testid="linkedin-abhilash"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-900 text-white" data-testid="our-values">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">Our Core Values</h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-secondary mb-3">Collaboration</div>
              <p className="text-white/80">Working together with clients, partners, and team members to achieve shared goals</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-secondary mb-3">Innovation</div>
              <p className="text-white/80">Continuously evolving our services to meet changing market needs and client expectations</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-secondary mb-3">Trust</div>
              <p className="text-white/80">Building lasting relationships through transparency, integrity, and consistent delivery</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary" data-testid="about-cta">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Let's work together to turn your real estate and financial goals into reality
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a href="/properties">
              <button className="px-8 py-4 bg-white text-primary rounded-lg font-semibold hover:bg-white/90 transition-all">
                Explore Properties
              </button>
            </a>
            <a href="tel:+918111954355">
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary transition-all">
                Contact Us
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
